import { useAnalytics } from "@codat/analytics";
import { Typography, useToast } from "@codat/orchard-ui";
import { AlertingRule, useDeleteRuleMutation } from "api/public";
import React from "react";

import Button from "components/Button";
import Modal from "components/Modal";
import ModalBody from "components/ModalBody";

import { formatRuleName } from "../WebhookRulesTable/utils";
import styles from "./DeleteRuleModal.module.css";

interface DeleteRuleModalProps {
  rule: AlertingRule;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteRuleModal: React.FC<DeleteRuleModalProps> = ({
  onSuccess,
  rule,
  onClose,
}) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const [{ loading: isDeleting }, deleteRule] = useDeleteRuleMutation({
    ruleId: rule.id,
  });

  const handleDeleteRule = () => {
    sendEvent("Delete Button - Click");
    deleteRule()
      .then(() => {
        addToast({
          message: `Deleted ${rule.type} rule`,
          variant: "success",
        });
        onSuccess();
      })
      .catch((error) => {
        addToast({
          message: `Unable to delete rule: ${error}`,
          variant: "error",
        });
      });
  };

  return (
    <Modal onClose={onClose} isPinned>
      {rule && (
        <ModalBody
          size="medium"
          title="Delete alerting rule?"
          className={styles.card}
          data-testid="delete-rule-modal"
          onClose={onClose}
        >
          <>
            <div className={styles.body} data-testid="delete-rule-modal-body">
              <Typography variant="body_md" as="p">
                Are you sure you want to delete your{" "}
                <span className={styles.bold}>{formatRuleName(rule.type)}</span>{" "}
                rule?
              </Typography>
              <Typography
                data-testid="delete-rule-modal-body"
                variant="body_md"
                as="p"
              >
                ID: {rule.id}
              </Typography>
            </div>
            <div className={styles.footerContainer}>
              <Button
                data-testid="cancel-button"
                className={styles.cancelButton}
                label="Cancel"
                buttonName="Cancel"
                variant="secondary"
                color="interface"
                onClick={onClose}
              />
              <Button
                data-testid="confirm-button"
                className={styles.submitButton}
                label="Delete rule"
                buttonName="Delete"
                variant="secondary"
                color="negative"
                onClick={handleDeleteRule}
                isLoading={isDeleting}
              />
            </div>
          </>
        </ModalBody>
      )}
    </Modal>
  );
};

export default DeleteRuleModal;
